import { useRef, useEffect, useCallback } from 'react';

export interface UseAutoResizeTextareaOptions {
  /** Starting height in pixels (default: 24) */
  minHeight?: number;
  /** Maximum height in pixels before scrolling (default: 200) */
  maxHeight?: number;
  /** Whether to include padding in height calculations (default: true) */
  includePadding?: boolean;
  /** Whether to include border in height calculations (default: true) */
  includeBorder?: boolean;
}

export interface UseAutoResizeTextareaReturn {
  /** Ref to attach to the textarea element */
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  /** Function to manually trigger resize (useful for external content changes) */
  resize: () => void;
}

/**
 * Custom hook for auto-resizing textareas based on content
 *
 * @param options Configuration options for the auto-resize behavior
 * @returns Object containing textareaRef and resize function
 *
 * @example
 * ```tsx
 * import { useAutoResizeTextarea } from 'js/utils/hooks/useAutoResizeTextarea';
 *
 * const MyComponent = () => {
 *   const { textareaRef, resize } = useAutoResizeTextarea({
 *     minHeight: 24,
 *     maxHeight: 200
 *   });
 *
 *   return (
 *     <textarea
 *       ref={textareaRef}
 *       onChange={resize} // Trigger resize on content change
 *     />
 *   );
 * };
 * ```
 */
export const useAutoResizeTextarea = (
  options: UseAutoResizeTextareaOptions = {},
): UseAutoResizeTextareaReturn => {
  const {
    minHeight = 24,
    maxHeight = 200,
    includePadding = true,
    includeBorder = true,
  } = options;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const calculateHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Get the content height
    let contentHeight = textarea.scrollHeight;

    // Adjust for padding and border if needed
    if (!includePadding || !includeBorder) {
      const computedStyle = window.getComputedStyle(textarea);

      if (!includePadding) {
        const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
        const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
        contentHeight -= paddingTop + paddingBottom;
      }

      if (!includeBorder) {
        const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
        const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;
        contentHeight -= borderTop + borderBottom;
      }
    }

    // Apply min and max height constraints
    const newHeight = Math.max(minHeight, Math.min(contentHeight, maxHeight));
    textarea.style.height = `${newHeight}px`;

    // Handle overflow when content exceeds max height
    if (contentHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
      // Ensure we can scroll to see all content
      textarea.style.height = `${maxHeight}px`;
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }, [minHeight, maxHeight, includePadding, includeBorder]);

  const resize = useCallback(() => {
    calculateHeight();
  }, [calculateHeight]);

  // Auto-resize on mount and when options change
  useEffect(() => {
    resize();
  }, [resize]);

  return {
    textareaRef,
    resize,
  };
};
